import React from 'react';
import { fromPairs, map, find, head, get, isEmpty, isEqual, } from 'lodash';
import classnames from 'classnames';

// local dependencies
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { Form } from 'modules/form';
import { button as Button } from 'modules/button';
import { getLabel } from 'utils/getLabel';
import { Typeahead } from 'modules/typeahead';
import { MatchCriteria } from 'modules/matchCriteria';
import { widgetPreloader as WidgetPreloader } from 'ui/views/generic/widgetPreloader';
import { isInbound } from 'resolvers/callStateEntities';
import { dimensions } from 'config/dimensions/client-auth-criteria-grid';
import { inputField as InputField } from 'ui/views/generic/inputField';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { authFlowCallerIdentificationClient as AuthFlowCallerIdentificationClient } from 'ui/views/widgets/authFlowCallerIdentificationClient';
 *
 * // render
 * <AuthFlowCallerIdentificationClient propName='propValue'/>
 */

// caller detail section (editable)
const CallerDetailSectionEditable = ( props ) => {

	const callerDetails = props.componentData.sections.callerDetails;
	const fieldsData = callerDetails.fields;

	// extracting actions
	const cancelAction = find( callerDetails.actions, { name: 'cancel' } );
	const saveAction = find( callerDetails.actions, { name: 'save' } );

	return (
		<div className='ui-auth-flow-caller-identification-client__caller-details'>
			<div className='ui-auth-flow-caller-identification-client__caller-details__title'>
				{ getLabel( callerDetails.title ) }
			</div>

			<Form
				fields={ fromPairs( map( fieldsData, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
				values={ props.clientInfo } // default values
				onSubmit={ ( { values, isValid } ) => {

					if( isValid ) {
						props.onFormSubmit( {
							title: values.title,
							firstName: values.firstName,
							lastName: values.lastName,
						} );
					}
					
				} }
			>
				{ ( { fields, values, isValid, errors, onChange, onSumbit } = {} ) => {
					const classNameField = classnames( {
						'ui-auth-flow-caller-identification-client__caller-details__details-form__fields-input-editable': ! isEqual( props.mode, 'readonly' ),
						'ui-auth-flow-caller-identification-client__caller-details__details-form__fields-input-readonly': isEqual( props.mode, 'readonly' )
					} );

					return (
						<form
							onSubmit={ onSumbit }
							className='ui-auth-flow-caller-identification-client__caller-details__details-form'
						>
							{ /* fields */ }
							{ map( fields, ( field ) => {
								return (
									<field.view
										key={ field.name }
										className={ classNameField }
										name={ field.name }
										required={ field.required }
										placeholder={ field.placeholder }
										label={ field.title }
										onChange={ onChange }
										value={ values[ field.name ] }
										error={ errors[ field.name ] }
										mode={ props.mode }
									/>
								);
							} ) }

							{ /* submit/edit action */ }
							<Button
								id={ saveAction.name }
								disabled={ !isValid }
								type='submit'
								size='large'
								label={ saveAction.title }
								className='ui-auth-flow-caller-identification-client__caller-details__details-form__fields-button'
							/>

							{/* cancel action */}
							{ isEmpty( props.clientInfo ) || isEqual( props.mode, 'readonly' ) ? null : (
								<Button
									id={ cancelAction.name }
									size='large'
									outline={ true }
									title={ cancelAction.title }
									className='ui-auth-flow-caller-identification-client__caller-details__details-form__fields-button'
									onClick={ () => props.onToggleEditMode( false ) }
								/>
							) }
						</form>
					);
				} }
			</Form>
		</div>
	);
};

// set display name
CallerDetailSectionEditable.displayName = 'authFlowCallerIdentificationClientSuccess.CallerDetailSectionEditable';

/* ------------------ */

// caller detail section (readyonly)
const CallerDetailSectionReadonly = ( props ) => {

	const callerDetails = props.componentData.sections.callerDetails;
	const fieldsData = callerDetails.fields;

	// extracting actions
	const editAction = find( callerDetails.actions, { name: 'edit' } );

	return (
		<div className='ui-auth-flow-caller-identification-client__caller-details'>
			<div className='ui-auth-flow-caller-identification-client__caller-details__title'>
				{ getLabel( callerDetails.title ) }
			</div>
			
			<div className='ui-auth-flow-caller-identification-client__caller-details__readonly-view'>
				{ /* fields */ }
				{ map( fieldsData, ( field ) => {
					return (
						<InputField
							key={ field.name }
							className='ui-auth-flow-caller-identification-client__caller-details__readonly-view__field'
							name={ field.name }
							label={ field.title }
							value={ props.clientInfo[ field.name ] }
							mode='readonly'
						/>
					);
				} ) }

				{/* edit action */}
				<Button
					size='large'
					title={ editAction.title }
					onClick={ () => {
						props.onToggleEditMode( true );
					} }
				/>
			</div>
		</div>
	);
};

// set display name
CallerDetailSectionReadonly.displayName = 'authFlowCallerIdentificationClientSuccess.CallerDetailSectionReadonly';

/* ------------------ */

// client search section
const ClientSearch = ( props ) => {

	// extracting caller identification details and its fields
	const callerIdentification = props.componentData.sections.callerIdentification;
	const callerIdentificationFields = callerIdentification.fields;

	return map( callerIdentificationFields, ( field ) => {
		return (
			<div
				key={ field.name }
				className='ui-auth-flow-caller-identification-client__client-search'
			>
				{/* title */}
                <div className='ui-auth-flow-caller-identification-client__client-search__title'>
                    { getLabel( callerIdentification.title ) }
                </div>
				{/* Search bar */}
				<Typeahead
					className='field-value-search__typeahead'
					size='large'
					url={ get( field, 'url' ) }
					placeholder={ field.placeholder }
					onQueryChange={ props.onClientSearchQueryChange }
					onChange={ ( { selected } ) => {
						props.onSelectClientSearch( head( selected ) ); // update selected value
					} }
				/>
			</div>
		);
	} );
};

// set display name
ClientSearch.displayName = 'authFlowCallerIdentificationClientSuccess.ClientSearch';

/* ---------------------- */

// No password found section
const NoPasswordFound = ( props ) => {
	
	const titleNoPasswordFound = getLabel( get( find( props.dataForMatchCriteria.criteria, { name: 'password' } ), 'valueEmptyMessage' ) ) ;

	return (
		<div className='ui-auth-flow-caller-identification-client__no-password-found'>
			
			<div className='ui-auth-flow-caller-identification-client__no-password-found__message'>
				{ `${ titleNoPasswordFound } ` }
			</div>

			<Button
				className='ui-auth-flow-caller-identification-client__caller-verification__action'
				size='large'
				title={ props.action.title }
				onClick={ () => props.handleActionClick( {
					item: props.itemData,
					value: props.actionsForNext,
				} ) }
				postIconId='arrow'
				showPostIcon={true}
				iconWidth='20'
				iconHeight='30'
			/>
		</div>
	);
};

// set display name
NoPasswordFound.displayName = 'authFlowCallerIdentificationClientSuccess.NoPasswordFound';

/* ---------------------- */

// client match criteria section
const ClientCriteria = ( props ) => {

	// extract actions for next button
	const actions = get( props.dataForMatchCriteria, 'actions' );
	const action = !isInbound() ? find( actions, { name: 'nextOutbound' } ) : find( actions, { name: 'nextInbound' } );

	// check if password has a value or not
	const isPasswordFound = props.dataForMatchCriteria.values.password;

	return (
		isPasswordFound ? (
			<div className='ui-auth-flow-caller-identification-client__caller-verification'>
				{ /* Match Criteria Div */ }
				<div className='ui-auth-flow-caller-identification-client__caller-verification__matchCriteria'>
					<MatchCriteria
						onMarkVerified={ props.handleMarkVerifiedAndShowNext }
						data={ props.dataForMatchCriteria }
						saveCriteriaSelection={ props.saveCriteriaSelection }
						layout= { dimensions }
					/>
				</div>
				
				{ /* the partition */ }
				<div className='ui-auth-flow-caller-identification-client__caller-verification__partition'/>

				{ /* the next button */ }
				<Button
					className='ui-auth-flow-caller-identification-client__caller-verification__action'
					size='large'
					title={ action.title }
					disabled={ ! props.isMemberVerified }
					onClick={ () => props.handleActionClick( {
						item: props.clientInfo,
						value: props.actionsForNext,
					} ) }
					postIconId='arrow'
					showPostIcon={true}
					iconWidth='20'
					iconHeight='30'
				/>
			</div>
		) : (
			<NoPasswordFound {...props} itemData={ props.clientInfo } action={ action } />
		)
	);
};

// set display name
ClientCriteria.displayName = 'authFlowCallerIdentificationClientSuccess.ClientCriteria';

/* ---------------------------------------- */

// Success view
export const authFlowCallerIdentificationClientSuccess = ( props ) => {

    return (
        <div className='ui-auth-flow-caller-identification-client'>
	
			{ /* Caller Details form */ }
			{
				isEqual( props.mode, 'readonly' ) ? (
					<CallerDetailSectionReadonly { ...props } />
				) : (
					<CallerDetailSectionEditable { ...props } />
				)
			}

            { /* Search Clint Name */ }
            { ! props.isSearchClientVisible ? null : <ClientSearch { ...props } /> }
            
            { /* Match Criteria along with next button */ }
            { ! props.isCriteriaVisible ? null : <ClientCriteria { ...props } /> }
        </div>
    );
};

// set display name
authFlowCallerIdentificationClientSuccess.displayName = 'authFlowCallerIdentificationClientSuccess';

// set default props
authFlowCallerIdentificationClientSuccess.defaultProps = {};

// prop types
authFlowCallerIdentificationClientSuccess.propTypes = {};

/* ******************************************************* */

// preloader view
export const authFlowCallerIdentificationClientPreloader = () => {
	return (
		<div className='ui-auth-flow-caller-identification-client-preloader'>
			<WidgetPreloader />
		</div>
	);
};

// set display name
authFlowCallerIdentificationClientPreloader.displayName = 'authFlowCallerIdentificationClientPreloader';

// set default props
authFlowCallerIdentificationClientPreloader.defaultProps = {};

// prop types
authFlowCallerIdentificationClientPreloader.propTypes = {};

/* ********************************************************* */

export const authFlowCallerIdentificationClient = withNetworkState(
	authFlowCallerIdentificationClientPreloader,
	authFlowCallerIdentificationClientSuccess,
);
